import { type ReactNode, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '../ui/button.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover.tsx';

export interface FilterChipItem<F extends string> {
  /** Stable key; also the field the editor focuses when the chip is clicked. */
  id: F;
  label: string;
  editable: boolean;
}

interface FilterChipListProps<F extends string, C extends FilterChipItem<F>> {
  chips: C[];
  /** Shown when there are no chips. */
  emptyText: string;
  onRemove: (chip: C) => void;
  /** Editor body; call `close` after applying. Rendered fresh on each open. */
  renderEditor: (focusField: F | null, close: () => void) => ReactNode;
}

/**
 * Smart Search filter chips: click an editable chip to edit it, × to remove,
 * plus an "Add filter" popover. Entity pages supply the chips and the editor.
 */
export function FilterChipList<F extends string, C extends FilterChipItem<F>>(
  { chips, emptyText, onRemove, renderEditor }: FilterChipListProps<F, C>,
) {
  const [open, setOpen] = useState(false);
  const [focusField, setFocusField] = useState<F | null>(null);

  const openEditor = (field: F | null) => {
    setFocusField(field);
    setOpen(true);
  };

  return (
    <div className='flex flex-wrap items-center gap-2' aria-label='Active filters' role='group'>
      {chips.length === 0 && <span className='text-sm text-muted-foreground'>{emptyText}</span>}
      {chips.map((chip) => (
        <span
          key={chip.id}
          className='inline-flex items-center rounded-full border bg-secondary text-secondary-foreground text-sm'
        >
          {chip.editable
            ? (
              <button
                type='button'
                className='pl-3 pr-1 py-1 rounded-l-full hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                onClick={() => openEditor(chip.id)}
                aria-label={`Edit filter: ${chip.label}`}
              >
                {chip.label}
              </button>
            )
            : <span className='pl-3 pr-1 py-1'>{chip.label}</span>}
          <button
            type='button'
            className='px-2 py-1 rounded-r-full hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
            onClick={() =>
              onRemove(chip)}
            aria-label={`Remove filter: ${chip.label}`}
          >
            <X className='h-3.5 w-3.5' aria-hidden='true' />
          </button>
        </span>
      ))}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={() =>
              openEditor(null)}
          >
            <Plus className='h-4 w-4 mr-1' aria-hidden='true' /> Add filter
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-[28rem] max-w-[calc(100vw-2rem)]' align='start'>
          {/* Mounted only while open, so each opening starts from the current spec. */}
          {open && renderEditor(focusField, () =>
            setOpen(false))}
        </PopoverContent>
      </Popover>
    </div>
  );
}
